import { BlockBlobClient } from '@azure/storage-blob';
import { post } from '@rails/request.js';
import { hashBlob, HASH_CHUNK_SIZE } from './sha256.js';
import { postConfirmBatches } from './confirm_batches.js';
import { BATCH_MAX_FILES } from './env.js';

export const MINT_BATCH_MAX_FILES = BATCH_MAX_FILES;
const MINT_BATCH_MAX_BYTES = 2 * 1024 * 1024 * 1024;
const UPLOAD_BLOCK_SIZE = 32 * 1024 * 1024;
const BLOCK_CONCURRENCY = 4;
const FILE_CONCURRENCY = 4;

async function uploadBlob(sasUrl, file, contentType, onProgress, overwrite = false) {
  const client = new BlockBlobClient(sasUrl);
  const options = {
    blobHTTPHeaders: { blobContentType: contentType },
    blockSize: UPLOAD_BLOCK_SIZE,
    maxSingleShotSize: UPLOAD_BLOCK_SIZE,
    concurrency: BLOCK_CONCURRENCY,
    onProgress: (ev) => onProgress(ev.loadedBytes, file.size),
  };
  if (!overwrite) options.conditions = { ifNoneMatch: '*' };
  await client.uploadData(file, options);
}

function batchManifest(entries, maxFiles, maxBytes) {
  const batches = [];
  let current = [];
  let bytes = 0;

  for (const entry of entries) {
    const size = Number(entry.size) || 0;
    if (current.length > 0 && (current.length >= maxFiles || bytes + size > maxBytes)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(entry);
    bytes += size;
  }

  if (current.length > 0) batches.push(current);
  return batches;
}

async function runPool(tasks, concurrency) {
  let cursor = 0;
  let failure = null;
  const size = Math.max(1, Math.min(concurrency, tasks.length));
  const workers = [];

  for (let index = 0; index < size; index += 1) {
    workers.push(
      (async () => {
        for (;;) {
          if (failure) return;

          const next = cursor;
          cursor += 1;
          if (next >= tasks.length) return;

          try {
            await tasks[next]();
          } catch (error) {
            if (!failure) failure = error;
            return;
          }
        }
      })()
    );
  }

  await Promise.all(workers);
  if (failure) throw failure;
}

export async function wipeUpload(wipeUrl) {
  if (!wipeUrl) return false;

  const response = await post(wipeUrl, {
    body: JSON.stringify({}),
    contentType: 'application/json',
    responseKind: 'json',
  });

  if (!response.ok) {
    throw new Error(`wipe failed (${response.status})`);
  }

  return true;
}

async function wipeIfNeeded(wipeUrl, needsWipe) {
  if (!needsWipe || !wipeUrl) return;

  try {
    await wipeUpload(wipeUrl);
  } catch (error) {
    console.error('[vdr-upload] wipe failed', error);
  }
}

export async function uploadFiles({ items, createUrl, confirmUrl, wipeUrl, callbacks = {} }) {
  const onProgress = callbacks.onProgress || (() => {});
  const onStatus = callbacks.onStatus || (() => {});

  if (!items || items.length === 0) return { uploaded: 0, rejected: [] };

  const byPath = new Map();
  const manifest = [];

  for (const item of items) {
    let sha256 = item.sha256;
    if (!sha256) {
      onStatus(item.relativePath, 'hashing');
      sha256 = await hashBlob(item.file, HASH_CHUNK_SIZE);
    }
    byPath.set(item.relativePath, { file: item.file, sha256 });
    manifest.push({
      relative_path: item.relativePath,
      mime_type: item.file.type || '',
      sha256,
      size: item.file.size,
    });
  }

  const manifestByPath = new Map(manifest.map((entry) => [entry.relative_path, entry]));

  let mintSucceeded = false;
  let anyConfirmed = false;
  let uploadedCount = 0;
  const allRejected = [];
  const confirmedFiles = [];

  try {
    for (const chunk of batchManifest(manifest, MINT_BATCH_MAX_FILES, MINT_BATCH_MAX_BYTES)) {
      const response = await post(createUrl, {
        body: JSON.stringify({ files: chunk }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      if (!response.ok) throw new Error(`authorization failed (${response.status})`);

      mintSucceeded = true;

      const data = await response.json;
      const rejected = data.rejected || [];
      rejected.forEach((entry) => onStatus(entry.relative_path, `skipped (${entry.reason})`));
      allRejected.push(...rejected);

      const tasks = (data.uploads || []).map((upload) => async () => {
        const entry = byPath.get(upload.relative_path);
        if (!entry || !upload.url) {
          throw new Error(`missing upload target for ${upload.relative_path || 'unknown'}`);
        }

        const contentType = entry.file.type || 'application/octet-stream';

        onStatus(upload.relative_path, 'uploading');

        try {
          await uploadBlob(
            upload.url,
            entry.file,
            contentType,
            (uploaded, total) => onProgress(upload.relative_path, uploaded, total),
            Boolean(upload.overwrite)
          );
        } catch (error) {
          onStatus(upload.relative_path, 'failed');
          throw error;
        }

        const manifestEntry = manifestByPath.get(upload.relative_path);
        if (manifestEntry) confirmedFiles.push(manifestEntry);
        onStatus(upload.relative_path, 'done');
        uploadedCount += 1;
      });

      await runPool(tasks, FILE_CONCURRENCY);
    }

    if (confirmedFiles.length === 0) {
      throw new Error('No files uploaded');
    }

    await postConfirmBatches(post, confirmUrl, confirmedFiles, MINT_BATCH_MAX_FILES, () => {
      anyConfirmed = true;
    });

    return { uploaded: uploadedCount, rejected: allRejected };
  } catch (error) {
    await wipeIfNeeded(wipeUrl, mintSucceeded && !anyConfirmed);
    throw error;
  }
}
